
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
// @mui
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
// assets
import { UploadIllustration } from 'src/assets/illustrations';
import { LoadingButton } from '@mui/lab';
import Iconify from '../iconify';
import RejectionFiles from './errors-rejection-files';
import MultiFilePreview from './preview-multi-file';
import SingleFilePreview from './preview-single-file';


export default function Upload({
  maxSize,
  minSize,
  accept,
  disabled,
  multiple = false,
  error,
  helperText,
  file,
  onDelete,
  files,
  currentUser,
  thumbnail,
  onUpload,
  onRemove,
  onRemoveAll,
  onDownloadAll,
  onSingleDownload, // <-- Just pass this prop to the parent component
  isEdit,
  sx,
  typo,
  ...other
}) {
  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    maxSize: maxSize || 5 * 1024 * 1024, // fallback to 5MB if undefined
    minSize,
    accept,
    multiple,
    disabled,
    ...other,
  });

  const hasFile = !!file && !multiple;
  const hasFiles = !!files && multiple && !!files.length;
  const hasError = isDragReject || !!error;

  const renderPlaceholder = (
    <Stack spacing={1} alignItems="center" justifyContent="center" flexWrap="wrap">
      <Stack direction="row" alignItems="center" spacing={1} sx={{ textAlign: 'center' }}>
        <UploadIllustration sx={{ width: 50, height: 50 }} />
        <Typography variant="h6">{typo}</Typography>
      </Stack>
    </Stack>
  );

  const renderSinglePreview = (
    <SingleFilePreview imgUrl={typeof file === 'string' ? file : file?.preview} />
  );

  const removeSinglePreview = hasFile && onDelete && (
    <Tooltip title="Remove Single File" arrow>
      <IconButton
        size="small"
        onClick={onDelete}
        sx={{
          top: 16,
          right: 16,
          zIndex: 9,
          position: 'absolute',
          color: (theme) => alpha(theme.palette.common.white, 0.8),
          bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
          '&:hover': {
            bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48),
          },
        }}
      >
        <Iconify icon="mingcute:close-line" width={18} sx={{ color: '#dd1f00' }} />
      </IconButton>
    </Tooltip>
  );

  const renderMultiPreview = hasFiles && (
    <>
      <Box>
        <MultiFilePreview
          files={files}
          onRemove={onRemove}
          onSingleDownload={currentUser ? onSingleDownload:null} // Pass the onSingleDownload prop here
        />
      </Box>  
      <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
        {onRemoveAll && (
          <Button variant="soft" onClick={onRemoveAll} color="error">
            Remove All
          </Button>
        )}
        {onDownloadAll && currentUser && (
          <LoadingButton variant="soft" color="success" onClick={onDownloadAll}>
            Download All
          </LoadingButton>
        )}
      </Stack>
    </>
  );

  return (
    <Box sx={{ width: 1, position: 'relative', ...sx }}>
      <Box
        {...getRootProps()}
        sx={{
          p: 2,
          outline: 'none',
          borderRadius: 1,
          cursor: 'pointer',
          overflow: 'hidden',
          position: 'relative',
          bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
          border: (theme) => `1px dashed ${alpha(theme.palette.grey[500], 0.2)}`,
          transition: (theme) => theme.transitions.create(['opacity', 'padding']),
          '&:hover': {
            opacity: 0.72,
          },
          ...(isDragActive && {
            opacity: 0.72,
          }),
          ...(disabled && {
            opacity: 0.48,
            pointerEvents: 'none',
          }),
          ...(hasError && {
            color: 'error.main',
            bgcolor: 'error.lighter',
            borderColor: 'error.light',
          }),
          ...(hasFile && {
            padding: '20% 0',
          }),
        }}
      >
        <input {...getInputProps()} />
        {hasFile ? renderSinglePreview : renderPlaceholder}
      </Box>

      {removeSinglePreview}

      {helperText && helperText}

      <RejectionFiles
        fileRejections={fileRejections}
        maxSize={maxSize}
        minSize={minSize}
        acceptFile={accept}
      />

      {renderMultiPreview}
    </Box>
  );
}

Upload.propTypes = {
  disabled: PropTypes.object,
  currentUser: PropTypes.object,
  error: PropTypes.bool,
  file: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  files: PropTypes.array,
  helperText: PropTypes.object,
  multiple: PropTypes.bool,
  onDelete: PropTypes.func,
  onDownloadAll: PropTypes.func,
  onRemove: PropTypes.func,
  onRemoveAll: PropTypes.func,
  onUpload: PropTypes.func,
  maxSize: PropTypes.number,
  accept: PropTypes.object,
  minSize: PropTypes.number,
  sx: PropTypes.object,
  typo: PropTypes.string,
  thumbnail: PropTypes.bool,
  isEdit: PropTypes.bool,
  onSingleDownload: PropTypes.func, // <-- Removed requirement for internal logic
};
